import { useRef, useState, useEffect } from 'react';
import PageCanvas from './PageCanvas';
import { ImageOff } from 'lucide-react';

const THUMB_PREFERRED_SCALE = 0.3;
const THUMB_MAX_WIDTH = 168; // w-48 sidebar (192px) minus p-3 padding (24px)

/**
 * ThumbnailSidebar — Scrollable sidebar showing page thumbnails.
 *
 * Each thumbnail fetches its page's actual viewport dimensions so the
 * placeholder and rendered canvas match the real size — no wasted space
 * for non-standard page sizes like shipping labels or receipts.
 *
 * For wide pages that would overflow the sidebar at the preferred scale,
 * the scale is reduced so the thumbnail fits within THUMB_MAX_WIDTH.
 *
 * Disabled for files with >200 pages.
 */
export default function ThumbnailSidebar({ pdfDoc, numPages, currentPage, onPageSelect, disabled }) {
  if (disabled) {
    return (
      <div className="w-12 bg-gray-50 border-r border-gray-200 shrink-0 flex flex-col items-center pt-4">
        <div className="group relative">
          <ImageOff size={18} className="text-gray-400" />
          <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 bg-gray-800 text-white text-xs px-3 py-1.5 rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
            Thumbnails hidden for large files ({numPages.toLocaleString()} pages)
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-48 bg-gray-50 border-r border-gray-200 overflow-y-auto shrink-0">
      <div className="p-3 flex flex-col items-center gap-3">
        {Array.from({ length: numPages }, (_, i) => i + 1).map((pageNum) => (
          <LazyThumbnail
            key={pageNum}
            pdfDoc={pdfDoc}
            pageNum={pageNum}
            isActive={pageNum === currentPage}
            onSelect={() => onPageSelect(pageNum)}
          />
        ))}
      </div>
    </div>
  );
}

function LazyThumbnail({ pdfDoc, pageNum, isActive, onSelect }) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [thumbInfo, setThumbInfo] = useState(null); // { width, height, scale }

  // Fetch actual page dimensions and compute scale to fit sidebar
  useEffect(() => {
    let cancelled = false;
    pdfDoc.getPage(pageNum).then((page) => {
      if (cancelled) return;
      const vp1 = page.getViewport({ scale: 1.0 });
      const scale = Math.min(THUMB_PREFERRED_SCALE, THUMB_MAX_WIDTH / vp1.width);
      const vp = page.getViewport({ scale });
      setThumbInfo({ width: vp.width, height: vp.height, scale });
    });
    return () => { cancelled = true; };
  }, [pdfDoc, pageNum]);

  // Lazy-render canvas when near viewport
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <button
      ref={ref}
      onClick={onSelect}
      className={`group relative rounded-lg overflow-hidden border-2 transition-all ${
        isActive
          ? 'border-brand-blue shadow-md'
          : 'border-transparent hover:border-gray-300'
      }`}
      style={thumbInfo ? { width: `${thumbInfo.width}px` } : undefined}
    >
      {isVisible && thumbInfo ? (
        <PageCanvas
          pdfDoc={pdfDoc}
          pageNumber={pageNum}
          scale={thumbInfo.scale}
        />
      ) : (
        <div
          className="bg-gray-200 animate-pulse"
          style={
            thumbInfo
              ? { width: `${thumbInfo.width}px`, height: `${thumbInfo.height}px` }
              : { width: '168px', aspectRatio: '8.5 / 11' }
          }
        />
      )}
      <span
        className={`absolute bottom-1 left-1/2 -translate-x-1/2 text-xs px-2 py-0.5 rounded-full ${
          isActive
            ? 'bg-brand-blue text-white'
            : 'bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity'
        }`}
      >
        {pageNum}
      </span>
    </button>
  );
}
